import { useQueryClient } from "@tanstack/react-query";

import API from "../../..";
import { UpdateRoomErpIntegrationsError } from "../../../rooms/errors";
import { useMutationWrapper } from "../../wrappers";
import { roomDetailsKey, updateRoomSettingsKey } from "../room-query-keys";
import { useInternationalization, useNotification } from "@/lib/contexts";

export interface UpdateRoomSettingsMutationParams {
  roomId: string;
  isCustomerOrManagerUser: boolean;
}

export function useMutationUpdateRoomSettings({
  roomId,
  isCustomerOrManagerUser,
}: UpdateRoomSettingsMutationParams) {
  const { t } = useInternationalization("pages.rooms.alerts.update-room");
  const { success, error } = useNotification();

  const queryClient = useQueryClient();

  const invalidateQueryKeys = () => {
    queryClient.invalidateQueries({ queryKey: roomDetailsKey(roomId) });
  };

  const onError = async (err: Error) => {
    const isErpIntegrationsError = err instanceof UpdateRoomErpIntegrationsError;
    if (!isCustomerOrManagerUser) {
      const errorMessage = isErpIntegrationsError ? t("erp-integrations-error") : t("error");
      error(errorMessage);
    } else {
      !isErpIntegrationsError && error(t("error"));
      isErpIntegrationsError && success(t("success"));
    }

    isErpIntegrationsError && invalidateQueryKeys();
  };

  const onSuccess = async () => {
    success(t("success"));
    invalidateQueryKeys();
  };

  const { mutate, isPending, isSuccess } = useMutationWrapper({
    mutationKey: updateRoomSettingsKey(roomId),
    mutationFn: API.Rooms.updateRoomSettings,
    onSuccess,
    onError,
  });

  return {
    mutate,
    isPending,
    isSuccess,
  };
}
